/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseControl from 'scripts/base-control';
import BaseMap from 'scripts/models/base/map';
import appModel from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';
import appContext from 'scripts/app-context';

const SHOW_ON_LAYOUT = ['extra-small', 'small', 'medium'];

const TABS_ACTIVE_CLASS = 'is-active';
const TABS_SELECTOR = '.m-home-selection-tabs__tab';
const TABS_SELECTOR_ACTIVE =
    '.m-home-selection-tabs__tab[data-column="{active}"]';

const COLUMNS_SELECTOR = '.{layoutClass}__column';
const COLUMNS_SELECTOR_ACTIVE = '.{layoutClass}__{active}';

// For easy reference
const keys = {
    left: 37,
    up: 38,
    right: 39,
    down: 40,
};

// Add or subtract depending on key pressed
const direction = {
    37: -1,
    38: -1,
    39: 1,
    40: 1,
};

export var HomeSelectionTabsModule = BaseControl.extend({
    init: function() {
        this._super();
        this.initModel();
        DomEventsManager.subscribe(this);
    },

    initModel: function() {
        this.model = new BaseMap({
            enabled: false,
            active: this.options.active,
            layoutClass: this.options.layoutClass,
        })
            .bind('active', () => {
                this.setActiveColumn();
            })
            .bind('enabled', () => {
                this.setTabsVisibility();
                this.setActiveColumn();
            });
    },

    initDom: function() {
        this.$tabs = this.$element.find(TABS_SELECTOR);
        this.$columns = this.$(this.can.sub(COLUMNS_SELECTOR, this.model));
        for (var i = 0; i < this.$tabs.length; ++i) {
            $(this.$tabs[i]).on(
                'keydown',
                function(e) {
                    this.switchTabOnArrowPress(e);
                }.bind(this),
            );
            this.$tabs[i].index = i;
        }
    },

    switchTabOnArrowPress: function(event) {
        const pressed = event.keyCode;

        if (direction[pressed]) {
            const target = event.target;
            if (target.index !== undefined) {
                if (this.$tabs[target.index + direction[pressed]]) {
                    this.$tabs[target.index + direction[pressed]].focus();
                } else if (pressed === keys.left || pressed === keys.up) {
                    this.focusLastTab();
                } else if (pressed === keys.right || pressed === keys.down) {
                    this.focusFirstTab();
                }
            }
        }
    },

    focusFirstTab: function() {
        this.$tabs[0].focus();
    },

    focusLastTab: function() {
        this.$tabs[this.$tabs.length - 1].focus();
    },

    onDocumentReady: function() {
        this.initDom();
    },

    onLayout: function() {
        this.model.attr(
            'enabled',
            SHOW_ON_LAYOUT.indexOf(appModel.model.screen.screenSize) > -1,
        );
    },

    setActiveColumn: function() {
        if (this.model.attr('enabled')) {
            let activeTab = this.can.sub(TABS_SELECTOR_ACTIVE, this.model);
            let activeColumn = this.can.sub(
                COLUMNS_SELECTOR_ACTIVE,
                this.model,
            );

            this.$tabs
                .removeClass(TABS_ACTIVE_CLASS)
                .attr('aria-selected', false)
                .attr('tabindex', '-1');
            this.$element
                .find(activeTab)
                .addClass(TABS_ACTIVE_CLASS)
                .attr('aria-selected', true)
                .removeAttr('tabindex');

            this.$columns.toggle(false);
            this.$(activeColumn).toggle(true);
            appContext.model.dispatch('home-selection-tabs-toggle', [
                activeColumn,
            ]);
        }
    },

    setTabsVisibility: function() {
        this.$columns.toggle(!this.model.attr('enabled'));
    },

    [TABS_SELECTOR + ' click']: function(el) {
        this.model.attr('active', this.$(el).data().column);
    },
});
